/**
 * Тела вкладок карточки клиента консультанта (AK-979).
 *
 * Low-fi утверждён founder 21.10 (PR #1176,
 * `docs/superpowers/prototypes/2026-10-21-client-card/`): «Обзор» из 8 блоков
 * и шесть вкладок. Каждая вкладка сама грузит свою ручку и сама показывает
 * три состояния — загрузку, «нет данных из CRM» (404) и «источник не отвечает»
 * (502/503, с «Повторить»): одна упавшая вкладка не гасит остальные.
 *
 * Чего нет в источнике — не рисуем цифрами: плана продаж по клиенту нет
 * нигде, отгрузок 1С нет у бота, опытов по клиенту консультанта нет в API.
 * Об этом сказано словами на месте, где человек ищет цифру.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Stack } from '@/components/ui/stack';
import { Tile, TileGrid } from '@/components/ui/tile-grid';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { api } from '@/lib/api';
import { getClientFieldsSummary } from '@/lib/consultant/api';
import {
  CARD_ERROR_TEXT,
  cardErrorKind,
  contactLine,
  daysAgoIso,
  formatDate,
  formatHa,
  formatQty,
  formatRub,
  getClientBotInspections,
  getClientSales,
  getClientVisits,
  pricesLabel,
  webInspectionFinding,
  type CardErrorKind,
  type ClientCard,
  type ClientCardTab,
  type ClientSystemState,
} from '@/lib/consultant/clientCard';
import { getSppOverview, type SppOverview } from '@/lib/planning/sppOverview';
import type { ClientFieldsSummary } from '@/lib/consultant/types';

// ─────────────────────────── общее ───────────────────────────

/** Загрузка одной ручки с «Повторить»; ответ на устаревший запрос игнорируем. */
export function useLoad<T>(load: () => Promise<T>, deps: unknown[]) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<CardErrorKind | null>(null);
  const [loading, setLoading] = useState(true);
  const req = useRef(0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const run = useCallback(load, deps);
  const reload = useCallback(() => {
    const id = ++req.current;
    setLoading(true);
    setError(null);
    run()
      .then((d) => {
        if (id === req.current) setData(d);
      })
      .catch((e: unknown) => {
        if (id === req.current) setError(cardErrorKind(e));
      })
      .finally(() => {
        if (id === req.current) setLoading(false);
      });
  }, [run]);
  useEffect(() => {
    reload();
  }, [reload]);
  return { data, error, loading, reload };
}

export function LoadingBlock({ testId }: { testId: string }) {
  return (
    <Stack gap={3} data-testid={testId}>
      <Skeleton className="h-8 w-full" />
      <Skeleton className="h-32 w-full" />
    </Stack>
  );
}

/**
 * Ошибка источника словами. «Повторить» — только там, где повтор поможет
 * (бот не ответил); 404 повтором не лечится, кнопка там была бы обманом.
 */
export function ErrorBlock({
  kind,
  onRetry,
  testId,
}: {
  kind: CardErrorKind;
  onRetry: () => void;
  testId: string;
}) {
  if (kind === 'not_found') {
    return (
      <Card data-testid={`${testId}-not-found`}>
        <CardContent className="p-4 text-sm text-muted-foreground">
          {CARD_ERROR_TEXT.not_found} Как только бот продаж начнёт отдавать
          карточку, данные появятся здесь сами.
        </CardContent>
      </Card>
    );
  }
  return (
    <Alert variant="destructive" data-testid={`${testId}-error`}>
      <AlertDescription className="flex flex-wrap items-center justify-between gap-3">
        <span>{CARD_ERROR_TEXT[kind]}</span>
        <Button size="sm" variant="outline" onClick={onRetry} data-testid={`${testId}-retry`}>
          <RefreshCw className="mr-2 h-3.5 w-3.5" />
          Повторить
        </Button>
      </AlertDescription>
    </Alert>
  );
}

function EmptyState({ testId, children }: { testId: string; children: React.ReactNode }) {
  return (
    <Card data-testid={testId}>
      <CardContent className="p-6 text-center text-sm text-muted-foreground">{children}</CardContent>
    </Card>
  );
}

/** Блок карточки: заголовок, источник данных, ссылка «Все →» на вкладку. */
function Section({
  title,
  source,
  links,
  testId,
  children,
}: {
  title: string;
  source: string;
  links?: { label: string; onClick: () => void }[];
  testId: string;
  children: React.ReactNode;
}) {
  return (
    <Card data-testid={testId} className="min-w-0">
      <CardHeader className="p-4 pb-3">
        <div className="flex items-start justify-between gap-3">
          <Stack gap={1}>
            <CardTitle>{title}</CardTitle>
            <CardDescription>{source}</CardDescription>
          </Stack>
          {links && (
            <div className="flex shrink-0 gap-2">
              {links.map((l) => (
                <Button key={l.label} size="sm" variant="link" onClick={l.onClick}>
                  {l.label}
                </Button>
              ))}
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="p-4 pt-0">{children}</CardContent>
    </Card>
  );
}

function KvRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex min-h-10 items-center justify-between gap-3 border-b py-2 text-sm leading-6 last:border-0">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right">{value}</span>
    </div>
  );
}

/** Таблица, которая на узком экране листается вбок ВНУТРИ карточки, а не страница. */
function ScrollTable({ children }: { children: React.ReactNode }) {
  return <div className="w-full overflow-x-auto">{children}</div>;
}

function SourceBadge({ source }: { source: string }) {
  const label = source === 'bot' ? 'бот' : source === 'web' ? 'веб' : 'Bitrix';
  return <Badge variant="outline">{label}</Badge>;
}

const dash = (v: string | null | undefined) => (v && v.trim() ? v : '—');

// ─────────────────────────── Обзор ───────────────────────────

export function OverviewTab({
  card,
  onOpenTab,
}: {
  card: ClientCard;
  onOpenTab: (tab: ClientCardTab) => void;
}) {
  const r = card.requisites;
  const regionLine = [r.region, r.district ?? r.city].filter(Boolean).join(', ');
  const cropsTotal = card.area.crops.reduce((s, c) => s + c.ha, 0);
  const competitors = useMemo(() => {
    const counts = new Map<string, number>();
    for (const m of card.competitors) counts.set(m.competitor, (counts.get(m.competitor) ?? 0) + 1);
    return [...counts.entries()]
      .sort((a, b) => a[0].localeCompare(b[0], 'ru'))
      .map(([name, n]) => `${name} — ${n}`)
      .join(', ');
  }, [card.competitors]);
  const salesLines = useMemo(
    () => [...card.sales.lines].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '', 'ru')),
    [card.sales.lines],
  );
  const cur = card.sales.cur_season ?? 'текущий';
  const prev = card.sales.prev_season ?? 'прошлый';
  const sys = card.system;

  return (
    <div className="grid min-w-0 gap-6 lg:grid-cols-2" data-testid="tab-overview">
      <Section title="1 · Реквизиты и менеджер" source="Bitrix" testId="block-requisites">
        <KvRow label="ИНН" value={dash(card.inn)} />
        <KvRow label="Регион, район" value={dash(regionLine)} />
        <KvRow label="Статус клиента" value={dash(r.client_status)} />
        <KvRow
          label="Приоритет"
          value={r.priority_letter ?? (r.is_priority ? 'приоритетный' : '—')}
        />
        <KvRow label="Размер хозяйства" value={dash(r.farm_size_category)} />
        <KvRow label="Направление" value={dash(r.crop_direction)} />
        <KvRow label="Менеджер" value={dash(card.manager.name)} />
        <KvRow
          label="Отдел"
          value={dash(sys.cluster_name ?? sys.department_path?.split('›').pop()?.trim())}
        />
        <KvRow label="На контроле" value={r.on_control.length ? r.on_control.join(', ') : '—'} />
      </Section>

      <Section title="2 · Площадь и культуры" source="Bitrix" testId="block-area">
        {card.area.crops.length > 0 ? (
          <ScrollTable>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Культура</TableHead>
                  <TableHead className="text-right">Га</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {card.area.crops.map((c) => (
                  <TableRow key={c.crop}>
                    <TableCell>{c.crop}</TableCell>
                    <TableCell className="text-right">{formatHa(c.ha)}</TableCell>
                  </TableRow>
                ))}
                <TableRow className="font-medium">
                  <TableCell>Итого</TableCell>
                  <TableCell className="text-right">{formatHa(cropsTotal)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </ScrollTable>
        ) : (
          <KvRow label="Общая площадь, га" value={formatHa(card.area.total_ha)} />
        )}
        {card.goals.length > 0 && (
          <Stack gap={1} className="pt-3 text-sm">
            {card.goals.map((g) => (
              <div key={g.label}>
                <div className="font-medium">{g.label}</div>
                <div>{g.text}</div>
              </div>
            ))}
          </Stack>
        )}
      </Section>

      <Section title="3 · Контакты и ЛПР" source="Bitrix" testId="block-contacts">
        <KvRow label="Собственник" value={contactLine(card.contacts.owner)} />
        <KvRow label="Руководитель" value={contactLine(card.contacts.head)} />
        <KvRow label="Агроном" value={contactLine(card.contacts.agro)} />
        {card.contacts.other.length > 0 && (
          <KvRow
            label="Прочие"
            value={card.contacts.other.map((c) => [c.name, c.post].filter(Boolean).join(', ')).join('; ')}
          />
        )}
      </Section>

      <Section
        title="4 · История встреч"
        source="Bitrix · последние 5"
        testId="block-meetings"
        links={[{ label: 'Все →', onClick: () => onOpenTab('visits') }]}
      >
        <Stack gap={3} className="text-sm">
          {card.meetings.last.length === 0 && (
            <p className="text-muted-foreground">Встреч в Bitrix пока нет.</p>
          )}
          {card.meetings.last.slice(0, 5).map((v, i) => (
            <div key={v.id ?? i}>
              <div className="text-xs text-muted-foreground">
                {formatDate(v.date)} · {dash(v.manager.name)}
              </div>
              <div>{v.agreed ?? v.text ?? v.subject ?? '—'}</div>
            </div>
          ))}
          {card.meetings.planned[0] && (
            <KvRow
              label="Запланирована"
              value={`${formatDate(card.meetings.planned[0].date)} · ${dash(card.meetings.planned[0].type_name)}`}
            />
          )}
          {competitors && (
            <div>
              <div className="font-medium">Конкуренты в отчётах за год</div>
              <div>{competitors}</div>
            </div>
          )}
        </Stack>
      </Section>

      <Section
        title="5 · Продажи и отгрузки 1С"
        source={`1С · заказы сезонов ${prev} и ${cur}, по алфавиту; отгрузок в источнике нет`}
        testId="block-sales"
        links={[{ label: 'Все →', onClick: () => onOpenTab('sales') }]}
      >
        {salesLines.length === 0 ? (
          <p className="text-sm text-muted-foreground">Заказов в 1С за два сезона нет.</p>
        ) : (
          <ScrollTable>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Препарат</TableHead>
                  <TableHead className="text-right">{prev}, ₽</TableHead>
                  <TableHead className="text-right">{cur}, ₽</TableHead>
                  <TableHead className="text-right">Цена {cur}, ₽</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {salesLines.map((l, i) => (
                  <TableRow key={`${l.name}-${i}`}>
                    <TableCell>{dash(l.name)}</TableCell>
                    <TableCell className="text-right">{formatRub(l.prev_amount)}</TableCell>
                    <TableCell className="text-right">{formatRub(l.cur_amount)}</TableCell>
                    <TableCell className="text-right">{pricesLabel(l.cur_prices)}</TableCell>
                  </TableRow>
                ))}
                <TableRow className="font-medium">
                  <TableCell>Итого</TableCell>
                  <TableCell className="text-right">{formatRub(card.sales.totals.prev_amount)}</TableCell>
                  <TableCell className="text-right">{formatRub(card.sales.totals.cur_amount)}</TableCell>
                  <TableCell />
                </TableRow>
              </TableBody>
            </Table>
          </ScrollTable>
        )}
        {card.sales.unlabeled.orders > 0 && (
          <p className="pt-3 text-xs text-muted-foreground">
            Заказов без года учёта: {card.sales.unlabeled.orders} на{' '}
            {formatRub(card.sales.unlabeled.amount)} ₽ — не попали в сравнение.
          </p>
        )}
      </Section>

      <Section
        title="6 · Хозяйства, поля и осмотры"
        source={sys.farm_name ? '1 хозяйство · завёл консультант' : 'хозяйство не заведено'}
        testId="block-farms"
        links={[{ label: 'Все →', onClick: () => onOpenTab('inspections') }]}
      >
        {sys.farm_name ? (
          <KvRow label={sys.farm_name} value={`полей: ${sys.fields_count} · наше`} />
        ) : (
          <p className="text-sm text-muted-foreground">
            Хозяйство клиента в системе ещё не заведено — кнопка «Завести хозяйство» вверху.
          </p>
        )}
        <div className="pt-3 text-sm font-medium">Осмотры</div>
        {card.inspections.last.length === 0 ? (
          <p className="text-sm text-muted-foreground">Осмотров из бота пока нет.</p>
        ) : (
          <ScrollTable>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Дата</TableHead>
                  <TableHead>Поле</TableHead>
                  <TableHead>Культура</TableHead>
                  <TableHead>Кто · откуда</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {card.inspections.last.slice(0, 5).map((x) => (
                  <TableRow key={x.id}>
                    <TableCell>{formatDate(x.date ?? x.created_at)}</TableCell>
                    <TableCell>{dash(x.field_name)}</TableCell>
                    <TableCell>{dash(x.crop)}</TableCell>
                    <TableCell>{dash(x.author.name)} · бот</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ScrollTable>
        )}
      </Section>

      <Section
        title="7 · Опыты"
        source="наша система"
        testId="block-trials"
        links={[{ label: 'Все →', onClick: () => onOpenTab('trials') }]}
      >
        <p className="text-sm text-muted-foreground">{TRIALS_EMPTY_TEXT}</p>
      </Section>

      <Section
        title="8 · СПП и план продаж"
        source="наша система"
        testId="block-spp-plan"
        links={[
          { label: 'СПП →', onClick: () => onOpenTab('spp') },
          { label: 'План →', onClick: () => onOpenTab('plan') },
        ]}
      >
        <KvRow label="Полей в системе" value={sys.fields_count} />
        <KvRow label="План продаж клиенту" value="появится из годового плана" />
        <KvRow label="Факт сезона (заказы 1С)" value={`${formatRub(card.sales.totals.cur_amount)} ₽`} />
      </Section>
    </div>
  );
}

// ─────────────────────────── СПП ───────────────────────────

/**
 * СПП клиента — из ЕГО кабинета. Прочитать кабинет можно только из открытого
 * окна клиента; без окна показываем то, что видно снаружи (полей, га) и
 * говорим, где смотреть разбивку по культурам.
 */
export function SppTab({
  system,
  windowTenantId,
}: {
  system: ClientSystemState;
  windowTenantId: string | null;
}) {
  const inWindow = !!system.linked_tenant_id && windowTenantId === system.linked_tenant_id;
  const summary = useLoad<ClientFieldsSummary | null>(
    () =>
      system.crm_company_id && system.state === 'in_system'
        ? getClientFieldsSummary(system.crm_company_id)
        : Promise.resolve(null),
    [system.crm_company_id, system.state],
  );
  const spp = useLoad<SppOverview | null>(
    async () => {
      if (!inWindow) return null;
      const companies = await api.get<{ id: string }[]>('/api/v1/companies');
      const id = companies[0]?.id;
      return id ? getSppOverview({ level: 'company', id }) : null;
    },
    [inWindow],
  );

  if (system.state !== 'in_system') {
    return (
      <EmptyState testId="tab-spp-empty">
        Полей клиента в системе пока нет — СПП появится, когда у клиента будут
        заведены поля и посевы.
      </EmptyState>
    );
  }
  if (summary.loading || spp.loading) return <LoadingBlock testId="tab-spp-loading" />;

  const col = spp.data?.columns.find((c) => c.is_current) ?? spp.data?.columns[0];
  const rows = (spp.data?.rows ?? [])
    .filter((r) => col && (r.cells[col.key]?.area_ha ?? 0) > 0)
    .sort((a, b) => a.name.localeCompare(b.name, 'ru'));

  return (
    <Stack gap={6} data-testid="tab-spp">
      <TileGrid cols={2}>
        <Tile label="Полей" value={summary.data?.fields_count ?? system.fields_count} />
        <Tile label="Площадь полей, га" value={formatHa(summary.data?.area_ha ?? null)} />
      </TileGrid>
      {col && rows.length > 0 ? (
        <Section title={`Структура посевных · ${col.label}`} source="кабинет клиента" testId="spp-table">
          <ScrollTable>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Культура</TableHead>
                  <TableHead className="text-right">Га</TableHead>
                  <TableHead className="text-right">Доля</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.key}>
                    <TableCell>{r.name}</TableCell>
                    <TableCell className="text-right">{formatHa(r.cells[col.key].area_ha)}</TableCell>
                    <TableCell className="text-right">
                      {r.cells[col.key].pct === null ? '—' : `${Math.round(r.cells[col.key].pct! * 10) / 10}%`}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ScrollTable>
        </Section>
      ) : (
        <EmptyState testId="tab-spp-hint">
          {inWindow
            ? 'В кабинете клиента посевов текущего сезона пока нет.'
            : 'Разбивка по культурам видна из кабинета клиента: откройте его кнопкой «Показать на карте» вверху.'}
        </EmptyState>
      )}
    </Stack>
  );
}

// ─────────────────────────── Осмотры ───────────────────────────

interface WebInspection {
  id: string;
  field_id: string | null;
  inspected_at: string;
  inspector_name: string | null;
  ai_extracted: Record<string, unknown> | null;
  photos: unknown[] | null;
}

interface MergedInspection {
  key: string;
  date: string | null;
  field: string | null;
  crop: string | null;
  who: string | null;
  source: 'bot' | 'web';
  finding: string;
  photos: number;
}

/**
 * Осмотры из бота (ручка карточки) + из веба (наша система, кабинет клиента).
 * Веб-осмотры читаются только из открытого окна клиента — иначе их не видно,
 * и это сказано под таблицей, а не спрятано.
 */
export function InspectionsTab({
  externalId,
  system,
  windowTenantId,
}: {
  externalId: string;
  system: ClientSystemState;
  windowTenantId: string | null;
}) {
  const inWindow = !!system.linked_tenant_id && windowTenantId === system.linked_tenant_id;
  const bot = useLoad(() => getClientBotInspections(externalId), [externalId]);
  const web = useLoad<MergedInspection[]>(
    async () => {
      if (!inWindow) return [];
      const [list, fields] = await Promise.all([
        api.get<WebInspection[]>('/api/v1/inspections?limit=200'),
        api
          .get<{ items?: { id: string; name: string }[] }>('/api/v1/fields?limit=1000')
          .catch(() => ({ items: [] })),
      ]);
      const names = new Map((fields.items ?? []).map((f) => [f.id, f.name]));
      return list.map((x) => ({
        key: `web-${x.id}`,
        date: x.inspected_at,
        field: x.field_id ? (names.get(x.field_id) ?? null) : null,
        crop: null,
        who: x.inspector_name,
        source: 'web' as const,
        finding: webInspectionFinding(x.ai_extracted),
        photos: x.photos?.length ?? 0,
      }));
    },
    [inWindow],
  );

  if (bot.loading || web.loading) return <LoadingBlock testId="tab-inspections-loading" />;

  const botRows: MergedInspection[] = (bot.data?.items ?? []).map((x) => ({
    key: `bot-${x.id}`,
    date: x.date ?? x.created_at,
    field: x.field_name,
    crop: x.crop,
    who: x.author.name,
    source: 'bot',
    finding: x.result_text ?? x.recommendations ?? '—',
    photos: x.photos_count,
  }));
  const rows = [...botRows, ...(web.data ?? [])].sort((a, b) =>
    (b.date ?? '').localeCompare(a.date ?? ''),
  );

  return (
    <Stack gap={4} data-testid="tab-inspections">
      {bot.error && bot.error !== 'not_found' && (
        <ErrorBlock kind={bot.error} onRetry={bot.reload} testId="inspections-bot" />
      )}
      {web.error && <ErrorBlock kind={web.error} onRetry={web.reload} testId="inspections-web" />}
      <Section
        title="Осмотры полей клиента"
        source={`наша система (веб) + бот · ${rows.length} осмотров`}
        testId="inspections-table"
      >
        {rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {bot.error === 'not_found' ? `${CARD_ERROR_TEXT.not_found} ` : ''}
            Осмотров пока нет.
          </p>
        ) : (
          <ScrollTable>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Дата</TableHead>
                  <TableHead>Поле</TableHead>
                  <TableHead>Культура</TableHead>
                  <TableHead>Кто</TableHead>
                  <TableHead>Откуда</TableHead>
                  <TableHead>Что нашли</TableHead>
                  <TableHead className="text-right">Фото</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((x) => (
                  <TableRow key={x.key} data-testid={`inspection-${x.key}`}>
                    <TableCell>{formatDate(x.date)}</TableCell>
                    <TableCell>{dash(x.field)}</TableCell>
                    <TableCell>{dash(x.crop)}</TableCell>
                    <TableCell>{dash(x.who)}</TableCell>
                    <TableCell>
                      <SourceBadge source={x.source} />
                    </TableCell>
                    <TableCell>{x.finding}</TableCell>
                    <TableCell className="text-right">{x.photos || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ScrollTable>
        )}
        {!inWindow && (
          <p className="pt-3 text-xs text-muted-foreground">
            Осмотры из веба лежат в кабинете клиента и видны здесь, когда кабинет открыт.
          </p>
        )}
      </Section>
    </Stack>
  );
}

// ─────────────────────────── Визиты ───────────────────────────

/** Период — по длительности (это шкала, а не список сущностей). */
const PERIODS = [
  { value: '30', label: 'последние 30 дней' },
  { value: '90', label: 'последние 90 дней' },
  { value: '365', label: 'последний год' },
  { value: 'all', label: 'за всё время' },
] as const;

export function VisitsTab({ externalId, now = new Date() }: { externalId: string; now?: Date }) {
  const [period, setPeriod] = useState<string>('90');
  const nowMs = now.getTime();
  const dateFrom = period === 'all' ? undefined : daysAgoIso(Number(period), new Date(nowMs));
  const done = useLoad(() => getClientVisits(externalId, 'done', dateFrom), [externalId, dateFrom]);
  const planned = useLoad(() => getClientVisits(externalId, 'planned'), [externalId]);

  const err = done.error ?? planned.error;
  if (err) {
    return (
      <ErrorBlock
        kind={err}
        onRetry={() => {
          done.reload();
          planned.reload();
        }}
        testId="visits"
      />
    );
  }

  return (
    <Stack gap={4} data-testid="tab-visits">
      <div className="flex flex-wrap items-center gap-3">
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-64" data-testid="visits-period">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PERIODS.map((p) => (
              <SelectItem key={p.value} value={p.value}>
                Период: {p.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {done.data && planned.data && (
          <span className="text-sm text-muted-foreground" data-testid="visits-count">
            {done.data.total} прошедших · {planned.data.total} запланировано
          </span>
        )}
      </div>
      {done.loading || planned.loading ? (
        <LoadingBlock testId="tab-visits-loading" />
      ) : (
        <>
          <Section title="Запланированные" source="Bitrix · дела, не закрыты" testId="visits-planned">
            {(planned.data?.items ?? []).length === 0 ? (
              <p className="text-sm text-muted-foreground">Запланированных визитов нет.</p>
            ) : (
              <ScrollTable>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Дата</TableHead>
                      <TableHead>Менеджер</TableHead>
                      <TableHead>Вид</TableHead>
                      <TableHead>Цель</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {planned.data!.items.map((v, i) => (
                      <TableRow key={v.id ?? i}>
                        <TableCell>{formatDate(v.date)}</TableCell>
                        <TableCell>{dash(v.manager.name)}</TableCell>
                        <TableCell>{dash(v.type_name)}</TableCell>
                        <TableCell>{dash(v.subject ?? v.text)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollTable>
            )}
          </Section>
          <Section title="Прошедшие" source="Bitrix и бот · итог — из отчёта менеджера" testId="visits-done">
            {(done.data?.items ?? []).length === 0 ? (
              <p className="text-sm text-muted-foreground">За этот период визитов нет.</p>
            ) : (
              <ScrollTable>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Дата</TableHead>
                      <TableHead>Менеджер</TableHead>
                      <TableHead>Откуда</TableHead>
                      <TableHead>Итог</TableHead>
                      <TableHead>Следующий шаг</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {done.data!.items.map((v, i) => (
                      <TableRow key={v.id ?? i}>
                        <TableCell>{formatDate(v.date)}</TableCell>
                        <TableCell>{dash(v.manager.name)}</TableCell>
                        <TableCell>
                          <SourceBadge source={v.source} />
                        </TableCell>
                        <TableCell>{dash(v.agreed ?? v.text ?? v.subject)}</TableCell>
                        <TableCell>{dash(v.next_step)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollTable>
            )}
          </Section>
        </>
      )}
    </Stack>
  );
}

// ─────────────────────────── Продажи ───────────────────────────

export function SalesTab({ externalId }: { externalId: string }) {
  const sales = useLoad(() => getClientSales(externalId), [externalId]);
  if (sales.loading) return <LoadingBlock testId="tab-sales-loading" />;
  if (sales.error) return <ErrorBlock kind={sales.error} onRetry={sales.reload} testId="sales" />;
  const data = sales.data!;
  const seasons = [...data.seasons].sort((a, b) => a.season.localeCompare(b.season));
  const items = [...data.items].sort(
    (a, b) =>
      (a.name ?? '').localeCompare(b.name ?? '', 'ru') || (a.season ?? '').localeCompare(b.season ?? ''),
  );

  return (
    <Stack gap={6} data-testid="tab-sales">
      <TileGrid cols={seasons.length + 1 > 2 ? 3 : 2}>
        {seasons.map((s) => (
          <Tile
            key={s.season}
            label={`Заказы ${s.season}`}
            value={`${formatRub(s.amount)} ₽`}
            hint={`заказов: ${s.orders}`}
          />
        ))}
        <Tile
          label="Без года учёта"
          value={`${formatRub(data.unlabeled.amount)} ₽`}
          hint={`заказов: ${data.unlabeled.orders}`}
        />
      </TileGrid>
      <Section
        title="Заказы по препаратам и сезонам"
        source="1С · заказы и корректировки, по алфавиту · отгрузок в источнике нет"
        testId="sales-table"
      >
        {items.length === 0 ? (
          <p className="text-sm text-muted-foreground">Заказов в 1С нет.</p>
        ) : (
          <ScrollTable>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Препарат</TableHead>
                  <TableHead>Сезон</TableHead>
                  <TableHead className="text-right">Кол-во</TableHead>
                  <TableHead className="text-right">Сумма, ₽</TableHead>
                  <TableHead className="text-right">Цены, ₽ за ед.</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((x, i) => (
                  <TableRow key={`${x.name}-${x.season}-${i}`}>
                    <TableCell>{dash(x.name)}</TableCell>
                    <TableCell>{dash(x.season)}</TableCell>
                    <TableCell className="text-right">{formatQty(x.qty)}</TableCell>
                    <TableCell className="text-right">{formatRub(x.amount)}</TableCell>
                    <TableCell className="text-right">{pricesLabel(x.prices)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ScrollTable>
        )}
        <p className="pt-3 text-xs text-muted-foreground">
          {data.not_in_source.shipments ??
            'Отгрузок в источнике нет: бот забирает из 1С только заказы и корректировки.'}
        </p>
      </Section>
    </Stack>
  );
}

// ─────────────────────────── План продаж и Опыты ───────────────────────────

export function PlanTab() {
  return (
    <EmptyState testId="tab-plan-empty">
      План продаж по клиенту появится из годового плана.
    </EmptyState>
  );
}

export const TRIALS_EMPTY_TEXT =
  'Опыты по клиенту появятся вместе с разделом «Наука»: связь опытов с клиентом консультанта ещё не заведена.';

export function TrialsTab() {
  return <EmptyState testId="tab-trials-empty">{TRIALS_EMPTY_TEXT}</EmptyState>;
}
